import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import {
  Card,
  Chip,
  Grid,
  List,
  Badge,
  Stack,
  Button,
  ListItem,
  MenuItem,
  useTheme,
  TextField,
  CardHeader,
  IconButton,
  Pagination,
  Typography,
  CardContent,
  ListItemText,
  useMediaQuery
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { copyToClipboard } from 'src/utils/text.utils';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { EmailConnectionStatus } from 'src/services/email-sync/email-sync.types';
import { EventTypeStatus, BookableEventType } from 'src/services/calendar/calendar.types';
import { useGetEmailConnectionStateQuery } from 'src/services/email-sync/email-sync.service';
import {
  useGetBookableEventTypesQuery,
  useRemoveBookableEventTypeByIdMutation,
} from 'src/services/calendar/calendar.service';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import ListSkeleton from 'src/components/list/list-skeleton';

import EventTypeEditorModal from 'src/sections/account/components/event-type-editor-modal';
import ConnectEmailPlaceholder from 'src/sections/account/components/connect-email-placeholder';
import EventTypeAvailabilityModal from 'src/sections/account/components/event-type-availability-modal';

export default function AccountSchedulingManagement() {

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const authContext = useAuthContext();

  const editorControl = useBoolean();
  const settingsControl = useBoolean();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const [selectedEventType, setSelectedEventType] = useState<BookableEventType | null>(null);

  const { data: eventTypesData, isLoading: isLoadingEventTypes } = useGetBookableEventTypesQuery({
    page,
    per_page: perPage,
    search_query: searchQuery,
  });

  const { currentData: connectionState } = useGetEmailConnectionStateQuery();

  useEffect(() => {
    if (eventTypesData?.count) {
      setTotalCount(eventTypesData.count);
    }
  }, [eventTypesData?.count]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const isEmailConnectionActive = useMemo(() => {
    if (!connectionState) return false;
    return connectionState?.status === EmailConnectionStatus.ACTIVE
  }, [connectionState]);

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const [removeEventType, { isLoading: isRemoving }] = useRemoveBookableEventTypeByIdMutation();

  const canEditSettings = useState(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  })[0];

  const handleEditorOpenModal = (eventType?: BookableEventType) => {
    setSelectedEventType(eventType || null);
    editorControl.onTrue();
  };

  const handleEditorCloseModal = () => {
    editorControl.onFalse();
    setSelectedEventType(null);
  };

  const handleSettingsOpenModal = (eventType: BookableEventType) => {
    setSelectedEventType(eventType);
    settingsControl.onTrue();
  };

  const handleSettingsCloseModal = () => {
    settingsControl.onFalse();
    setSelectedEventType(null);
  };

  const handleDeleteEventType = async (eventTypeId: string) => {
    try {
      await removeEventType({ eventTypeId }).unwrap();
      enqueueSnackbar(t('account.scheduling_settings.api.delete.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('account.scheduling_settings.api.delete.default_error'), { variant: 'error' });
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} ${t('common.time.minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? t('common.time.hour') : t('common.time.hours')}`;
    }
    return `${hours} ${hours === 1 ? t('common.time.hour') : t('common.time.hours')} ${remainingMinutes} ${t('common.time.minutes')}`;
  };

  const copyEventTypeLink = (eventType: BookableEventType) => {

    if (!eventType.link) {
      return;
    }

    copyToClipboard(eventType.link)
      .then(() => {
        enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar(t('errors.default_error'), { variant: 'error' });
      });
  }

  if (!isEmailConnectionActive) {
    return (
      <ConnectEmailPlaceholder
        sx={{ mt: 3 }}
      />
    )
  }

  return (
    <Card sx={{ width: '100%', boxShadow: 0 }}>
      <CardHeader
        avatar={
          <Iconify
            icon="mdi:calendar-edit"
            sx={{
              width: 30,
              height: 30,
              color: theme.palette.primary.main,
            }}
          />
        }
        title={
          <Typography variant="h6">{t('account.scheduling_settings.title')}</Typography>
        }
        action={
          canEditSettings && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => handleEditorOpenModal()}
            >
              {t('account.scheduling_settings.add_event_type')}
            </Button>
          )
        }
      />

      <CardContent>
        <Stack spacing={2} sx={{ px: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="column" my={2} mb={4}>
                <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                  <Stack direction="row" gap={2} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('account.scheduling_settings.event_types')}
                    </Typography>

                    {eventTypesData?.results && eventTypesData.results.length > 0 && (
                      <Badge badgeContent={eventTypesData.results.length} color="primary" />
                    )}
                  </Stack>
                </Stack>

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('account.scheduling_settings.event_types_helper')}
                </Typography>
              </Stack>

              {isLoadingEventTypes ? (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <ListSkeleton />
                </Box>
              ) : (
                <>
                  {eventTypesData?.results && eventTypesData.results.length > 0 ? (
                    <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                      {eventTypesData.results.map((eventType: BookableEventType, index: number) => (
                        <ListItem
                          key={eventType.id}
                          secondaryAction={
                            canEditSettings && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                {eventType.status !== EventTypeStatus.ACTIVE && (
                                  <Chip
                                    icon={<Iconify icon="mdi:close-circle-outline" width={16} height={16} />}
                                    label={t('account.scheduling_settings.status.inactive')}
                                    size="small"
                                    color="default"
                                    variant="filled"
                                  />
                                )}

                                {eventType.status === EventTypeStatus.ACTIVE && (
                                  <IconButton
                                    edge="end"
                                    onClick={() => copyEventTypeLink(eventType)}
                                  >
                                    <Iconify icon="uil:copy" width={16} height={16} />
                                  </IconButton>
                                )}

                                <IconButton
                                  edge="end"
                                  color="primary"
                                  onClick={() => handleEditorOpenModal(eventType)}
                                >
                                  <Iconify icon="eva:edit-fill" />
                                </IconButton>

                                <IconButton
                                  edge="end"
                                  onClick={() => handleSettingsOpenModal(eventType)}
                                >
                                  <Iconify icon="mdi:settings-outline" width={16} height={16}
                                  />
                                </IconButton>

                                <IconButton
                                  edge="end"
                                  color="error"
                                  onClick={() => handleDeleteEventType(eventType.id)}
                                >
                                  <Iconify icon="eva:trash-2-outline" />
                                </IconButton>
                              </Stack>
                            )
                          }
                          divider={eventTypesData?.results && index < eventTypesData.results.length - 1}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle2">
                                  {eventType.name}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <Stack direction="column" spacing={0.5} mt={0.5}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Iconify icon="mdi:clock-outline" width={16} height={16} />
                                    <Typography variant="caption">
                                      {formatDuration(eventType.duration_minutes)}
                                    </Typography>
                                  </Stack>
                                  {eventType.buffer_minutes && eventType.buffer_minutes > 0 && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Iconify icon="mdi:clock-plus-outline" width={16} height={16} />
                                      <Typography variant="caption">
                                        {`${t('account.scheduling_settings.buffer')}: ${formatDuration(eventType.buffer_minutes)}`}
                                      </Typography>
                                    </Stack>
                                  )}
                                </Stack>
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <EmptyContent
                      title={t('account.scheduling_settings.no_event_types')}
                      description={t('account.scheduling_settings.no_event_types_description')}
                    />
                  )}

                  {/* Pagination */}
                  {eventTypesData?.results && eventTypesData.results.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Stack spacing={2} direction="row" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('common.rows_per_page')}:
                        </Typography>
                        <TextField
                          select
                          size="small"
                          value={perPage}
                          onChange={handlePerPageChange}
                          sx={{ minWidth: 70 }}
                        >
                          {[5, 10, 25, 50].map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">
                          {totalCount > 0 ?
                            `${(page - 1) * perPage + 1}-${Math.min(
                              page * perPage,
                              totalCount
                            )} ${t('common.of')} ${totalCount}` :
                            `0 ${t('common.of')} 0`}
                        </Typography>
                        <Pagination
                          count={Math.ceil(totalCount / perPage)}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          size="small"
                        />
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Stack>
      </CardContent>

      {
        editorControl.value && (
          <EventTypeEditorModal
            open={editorControl.value}
            onClose={handleEditorCloseModal}
            eventType={selectedEventType}
          />
        )
      }

      {
        settingsControl.value && (
          <EventTypeAvailabilityModal
            open={settingsControl.value}
            onClose={settingsControl.onFalse}
            eventType={selectedEventType as BookableEventType}
          />
        )
      }

    </Card>
  );
}